import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../../contexts/DataContext';
import { useInvestigation } from '../../../contexts/InvestigationContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { 
  FiArrowLeft, FiPieChart, FiUsers, FiShield, FiDollarSign, 
  FiCalendar, FiShare2, FiDownload, FiFilter, FiBriefcase, 
  FiAlertTriangle, FiCheckCircle, FiClock, FiFileText, FiRefreshCw, FiGlobe,
  FiTrendingUp, FiLayout
} = FiIcons;

const ReportingModule = () => {
  const navigate = useNavigate();
  const { companies = [], reports = [], invoices = [], bookings = [], partners = [], sellers = [] } = useData();
  const { investigations = [] } = useInvestigation();

  // --- STATE ---
  const [activeTab, setActiveTab] = useState('oversikt');
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [filterCountry, setFilterCountry] = useState('all');
  const [filterPlan, setFilterPlan] = useState('all');

  // --- CALCULATIONS ---
  const stats = useMemo(() => {
    const activeComps = companies.filter(c => c.active);
    const mrr = activeComps.reduce((sum, c) => sum + (c.monthly_fee || c.monthlyFee || 0), 0);
    const totalInvoiced = invoices.reduce((sum, inv) => sum + (inv.total_amount || inv.totalAmount || 0), 0);
    const totalPaid = invoices.filter(i => i.status === 'paid').reduce((sum, inv) => sum + (inv.total_amount || inv.totalAmount || 0), 0);
    
    return {
      totalCompanies: companies.length,
      activeCompanies: activeComps.length,
      newThisMonth: companies.filter(c => {
        const d = new Date(c.createdAt || c.created_at);
        return d.getMonth() === new Date().getMonth() && d.getFullYear() === new Date().getFullYear();
      }).length,
      mrr,
      arr: mrr * 12,
      totalInvoiced,
      totalPaid,
      outstanding: totalInvoiced - totalPaid,
      whistleReports: reports.filter(r => r.reportType !== 'ruh_report').length,
      ruhReports: reports.filter(r => r.reportType === 'ruh_report').length,
      investigations: investigations.length,
      totalBookings: bookings.length,
      conversionRate: bookings.length > 0 ? ((companies.length / bookings.length) * 100).toFixed(1) : 0
    };
  }, [companies, reports, invoices, bookings, investigations]);

  const tabs = [
    { id: 'oversikt', label: 'Oversikt', icon: FiPieChart },
    { id: 'kunder', label: 'Kunder & Abonnement', icon: FiUsers },
    { id: 'varsler', label: 'Varsler & RUH', icon: FiAlertTriangle },
    { id: 'utredninger', label: 'Utredninger', icon: FiShield },
    { id: 'finans', label: 'Faktura & Inntekter', icon: FiDollarSign },
    { id: 'booking', label: 'Booking & Demoer', icon: FiCalendar },
    { id: 'salg', label: 'Partnere & Selgere', icon: FiShare2 },
    { id: 'eksport', label: 'Eksport', icon: FiDownload }
  ];

  const handleExport = (format) => {
    try {
      if (format === 'csv' || format === 'excel') {
        // Excel in Norway prefers semicolon as separator. Standard CSV uses comma.
        const separator = format === 'csv' ? ',' : ';';
        const headers = ['Bedrift ID', 'Firma', 'Org.nr', 'Abonnement', 'Månedspris', 'Status', 'Registrert'];
        const csvRows = [headers.join(separator)];
        
        const safeCompanies = Array.isArray(companies) ? companies : [];
        
        safeCompanies.forEach(c => {
          const id = `"${c.id}"`;
          const name = `"${(c.name || '').replace(/"/g, '""')}"`;
          const org = `"${c.org_number || c.orgNumber || ''}"`;
          const sub = `"${c.subscription || 'Standard'}"`;
          const fee = c.monthly_fee || c.monthlyFee || 0;
          const status = c.active ? 'Aktiv' : 'Inaktiv';
          const regDate = new Date(c.createdAt || c.created_at).toLocaleDateString('no-NO');
          
          csvRows.push([id, name, org, sub, fee, status, regDate].join(separator));
        });
        
        // Add BOM for UTF-8 compatibility in Excel so special characters like ÆØÅ works perfectly
        const csvString = '\uFEFF' + csvRows.join('\n'); 
        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `Eksport_Kunder_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
      } else if (format === 'pdf') {
        const safeCompanies = Array.isArray(companies) ? companies : [];
        const printContent = `
          <html>
            <head>
              <title>Eksport Rapport</title>
              <style>
                body { font-family: Arial, sans-serif; padding: 20px; color: #333; }
                h1 { color: #1e3a8a; }
                table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 12px; }
                th, td { border: 1px solid #e2e8f0; padding: 10px; text-align: left; }
                th { background-color: #f8fafc; font-weight: bold; color: #1e293b; }
                tr:nth-child(even) { background-color: #f8fafc; }
                .status-active { color: #16a34a; font-weight: bold; }
                .status-inactive { color: #dc2626; font-weight: bold; }
              </style>
            </head>
            <body>
              <h1>HMSVarsling - Kunderapport</h1>
              <p>Generert: ${new Date().toLocaleString('no-NO')}</p>
              <p>Totalt antall bedrifter: ${safeCompanies.length}</p>
              <table>
                <thead>
                  <tr>
                    <th>Firma</th>
                    <th>Org.nr</th>
                    <th>Abonnement</th>
                    <th>Status</th>
                    <th>Registrert</th>
                  </tr>
                </thead>
                <tbody>
                  ${safeCompanies.map(c => `
                    <tr>
                      <td>${c.name}</td>
                      <td>${c.org_number || c.orgNumber || '-'}</td>
                      <td style="text-transform: capitalize;">${c.subscription || 'Standard'}</td>
                      <td class="${c.active ? 'status-active' : 'status-inactive'}">${c.active ? 'Aktiv' : 'Inaktiv'}</td>
                      <td>${new Date(c.createdAt || c.created_at).toLocaleDateString('no-NO')}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </body>
          </html>
        `;
        
        const printWindow = window.open('', '_blank');
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.focus();
        // Wait shortly to let the dom render before prompting print
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 500);
      }
    } catch (err) {
      console.error("Export error:", err);
      alert("En feil oppstod under eksporten.");
    }
  };

  const ReportKPI = ({ title, value, subValue, icon: Icon, color }) => (
    <div className="bg-white p-6 rounded-3xl border border-blue-50 shadow-sm flex items-center gap-5">
      <div className={`w-14 h-14 bg-${color}-50 text-${color}-600 rounded-2xl flex items-center justify-center`}>
        <SafeIcon icon={Icon} size={24} />
      </div>
      <div>
        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">{title}</p>
        <p className="text-2xl font-black text-blue-950">{value}</p>
        {subValue && <p className="text-[10px] font-bold text-gray-400 mt-1">{subValue}</p>}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F1F5F9] font-sans text-blue-950">
      <header className="bg-white border-b sticky top-0 z-40 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/superadmin')} className="p-2.5 hover:bg-blue-50 rounded-xl transition-all text-blue-900 border border-transparent hover:border-blue-100">
              <SafeIcon icon={FiArrowLeft} size={20} />
            </button>
            <div>
              <h1 className="text-xl font-black text-blue-950 uppercase tracking-tight">Rapportering & Innsikt</h1>
              <p className="text-[10px] font-bold text-blue-400 uppercase tracking-widest">Plattform-kontrollpanel</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
             <button onClick={() => window.location.reload()} className="p-3 text-blue-400 hover:text-blue-600 transition-colors">
              <SafeIcon icon={FiRefreshCw} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto p-8 space-y-8">
        {/* GLOBAL FILTERS */}
        <div className="bg-white p-6 rounded-[32px] border border-blue-50 shadow-sm flex flex-wrap gap-4 items-center">
          <div className="flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-xl">
            <SafeIcon icon={FiCalendar} className="text-blue-400" />
            <input type="date" className="bg-transparent border-none text-xs font-bold uppercase outline-none" />
            <span className="text-gray-300">-</span>
            <input type="date" className="bg-transparent border-none text-xs font-bold uppercase outline-none" />
          </div>
          <select value={filterCountry} onChange={e => setFilterCountry(e.target.value)} className="bg-blue-50 border-none rounded-xl px-4 py-3 text-xs font-bold uppercase text-blue-900 outline-none">
            <option value="all">Alle Land</option>
            <option value="NO">Norge</option>
            <option value="SE">Sverige</option>
            <option value="DK">Danmark</option>
          </select>
          <select value={filterPlan} onChange={e => setFilterPlan(e.target.value)} className="bg-blue-50 border-none rounded-xl px-4 py-3 text-xs font-bold uppercase text-blue-900 outline-none">
            <option value="all">Alle Pakker</option>
            <option value="standard">Standard</option>
            <option value="konsern">Konsern</option>
            <option value="grunnpakke">Grunnpakke</option>
          </select>
        </div>

        {/* TABS */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-3 px-6 py-4 rounded-2xl text-sm font-black uppercase tracking-wider transition-all whitespace-nowrap ${activeTab === tab.id ? 'bg-blue-800 text-white shadow-xl shadow-blue-900/20' : 'bg-white text-gray-500 hover:bg-blue-50 hover:text-blue-800'}`}
            >
              <SafeIcon icon={tab.icon} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* TAB CONTENT */}
        <div className="space-y-8">
          
          {/* OVERSIKT */}
          {activeTab === 'oversikt' && (
            <motion.div initial={{opacity: 0, y: 20}} animate={{opacity: 1, y: 0}} className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <ReportKPI title="Totale Bedrifter" value={stats.totalCompanies} subValue={`${stats.activeCompanies} aktive`} icon={FiBriefcase} color="blue" />
                <ReportKPI title="MRR (Månedsinntekt)" value={`${stats.mrr.toLocaleString()} kr`} subValue={`ARR: ${stats.arr.toLocaleString()} kr`} icon={FiDollarSign} color="green" />
                <ReportKPI title="Nye Kunder (Måned)" value={stats.newThisMonth} subValue="Siste 30 dager" icon={FiUsers} color="indigo" />
                <ReportKPI title="Konvertering" value={`${stats.conversionRate}%`} subValue="Demo til Kunde" icon={FiTrendingUp} color="orange" />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-8 rounded-[40px] shadow-sm border border-blue-50">
                  <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-6">Saker & Aktivitet</h3>
                  <div className="space-y-6">
                    <div className="flex justify-between items-end">
                      <span className="text-sm font-bold text-gray-600">Varsler</span>
                      <span className="text-2xl font-black">{stats.whistleReports}</span>
                    </div>
                    <div className="flex justify-between items-end">
                      <span className="text-sm font-bold text-gray-600">RUH</span>
                      <span className="text-2xl font-black">{stats.ruhReports}</span>
                    </div>
                    <div className="flex justify-between items-end">
                      <span className="text-sm font-bold text-gray-600">Utredninger</span>
                      <span className="text-2xl font-black">{stats.investigations}</span>
                    </div>
                  </div>
                </div>
                
                <div className="md:col-span-2 bg-gradient-to-br from-blue-900 to-indigo-950 p-8 rounded-[40px] shadow-xl text-white relative overflow-hidden">
                  <div className="relative z-10">
                    <h3 className="text-xs font-black text-blue-300 uppercase tracking-widest mb-6">Finansiell Status</h3>
                    <div className="grid grid-cols-2 gap-10">
                      <div>
                        <p className="text-[10px] font-bold text-blue-300 uppercase mb-1">Totalt Fakturert</p>
                        <p className="text-3xl font-black">{stats.totalInvoiced.toLocaleString()} kr</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-blue-300 uppercase mb-1">Utestående Beløp</p>
                        <p className="text-3xl font-black text-orange-400">{stats.outstanding.toLocaleString()} kr</p>
                      </div>
                    </div>
                    <div className="mt-10 pt-10 border-t border-white/10 flex justify-between items-center">
                      <div>
                        <p className="text-[10px] font-bold text-blue-300 uppercase">Betalte Fakturaer</p>
                        <p className="text-xl font-bold">{stats.totalPaid.toLocaleString()} kr</p>
                      </div>
                      <div className="text-right">
                        <SafeIcon icon={FiCheckCircle} size={40} className="text-green-400" />
                      </div>
                    </div>
                  </div>
                  <div className="absolute -right-20 -top-20 w-64 h-64 bg-white/5 rounded-full blur-3xl"></div>
                </div>
              </div>
            </motion.div>
          )}

          {/* KUNDER & ABONNEMENT */}
          {activeTab === 'kunder' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white p-8 rounded-[32px] border border-blue-50 shadow-sm">
                  <h3 className="font-black uppercase tracking-tight mb-6">Kundestatus</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center p-4 bg-green-50 rounded-2xl">
                      <span className="font-bold text-green-800">Aktive Kunder</span>
                      <span className="text-2xl font-black text-green-600">{companies.filter(c => c.active).length}</span>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-red-50 rounded-2xl">
                      <span className="font-bold text-red-800">Inaktive Kunder</span>
                      <span className="text-2xl font-black text-red-600">{companies.filter(c => !c.active).length}</span>
                    </div>
                  </div>
                  
                  <h3 className="font-black uppercase tracking-tight mt-8 mb-4">Abonnementsfordeling</h3>
                  <div className="space-y-4">
                    {['standard', 'konsern', 'grunnpakke'].map(plan => {
                      const count = companies.filter(c => c.subscription === plan).length;
                      const percentage = companies.length > 0 ? (count / companies.length) * 100 : 0;
                      return (
                        <div key={plan} className="space-y-2">
                          <div className="flex justify-between text-sm font-bold">
                            <span className="capitalize">{plan}</span>
                            <span>{count} stk ({percentage.toFixed(0)}%)</span>
                          </div>
                          <div className="w-full bg-blue-50 h-3 rounded-full overflow-hidden">
                            <div className="bg-blue-600 h-full" style={{ width: `${percentage}%` }}></div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
                <div className="bg-white p-8 rounded-[32px] border border-blue-50 shadow-sm overflow-hidden flex flex-col">
                  <h3 className="font-black uppercase tracking-tight mb-6">Nyeste Kunder</h3>
                  <div className="flex-1 overflow-y-auto pr-2 space-y-3">
                    {companies.sort((a,b) => new Date(b.createdAt || b.created_at) - new Date(a.createdAt || a.created_at)).slice(0, 10).map(c => (
                      <div key={c.id} className="flex justify-between items-center p-3 hover:bg-gray-50 rounded-xl transition-colors border border-gray-100">
                        <div>
                          <p className="font-bold text-gray-900">{c.name}</p>
                          <p className="text-xs text-gray-500">{new Date(c.createdAt || c.created_at).toLocaleDateString('no-NO')}</p>
                        </div>
                        <span className="text-[10px] font-bold px-2 py-1 bg-blue-100 text-blue-700 rounded-lg capitalize tracking-widest">{c.subscription || 'Standard'}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* VARSLER & RUH */}
          {activeTab === 'varsler' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-8 rounded-[32px] border border-blue-50 shadow-sm">
                  <h3 className="font-black uppercase tracking-tight mb-6">Statusfordeling</h3>
                  <div className="space-y-4">
                    {['new', 'investigating', 'resolved'].map(status => {
                      const count = reports.filter(r => r.status === status).length;
                      const total = reports.length || 1;
                      const percentage = (count/total)*100;
                      return (
                        <div key={status} className="space-y-2">
                          <div className="flex justify-between text-sm font-bold">
                            <span className="capitalize">{status === 'new' ? 'Nye' : status === 'investigating' ? 'Under arbeid' : 'Løst'}</span>
                            <span>{count}</span>
                          </div>
                          <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                            <div className={`h-full ${status === 'new' ? 'bg-blue-500' : status === 'investigating' ? 'bg-orange-500' : 'bg-green-500'}`} style={{ width: `${percentage}%` }}></div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
                <div className="bg-white p-8 rounded-[32px] border border-blue-50 shadow-sm md:col-span-2">
                  <h3 className="font-black uppercase tracking-tight mb-6">Nylige Hendelser</h3>
                  <div className="space-y-3">
                    {reports.sort((a,b) => new Date(b.createdAt || b.created_at) - new Date(a.createdAt || a.created_at)).slice(0, 5).map(r => (
                      <div key={r.id} className="flex justify-between items-center p-3 hover:bg-gray-50 rounded-xl transition-colors border border-gray-100">
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${r.reportType === 'ruh_report' ? 'bg-orange-100 text-orange-600' : 'bg-red-100 text-red-600'}`}>
                            <SafeIcon icon={FiAlertTriangle} />
                          </div>
                          <div>
                            <p className="font-bold text-gray-900">{r.title || 'Uten tittel'}</p>
                            <p className="text-xs text-gray-500">{new Date(r.createdAt || r.created_at).toLocaleDateString('no-NO')}</p>
                          </div>
                        </div>
                        <span className={`text-[10px] font-black uppercase px-2 py-1 rounded-md ${
                            r.status === 'new' ? 'bg-blue-100 text-blue-700' : 
                            r.status === 'investigating' ? 'bg-yellow-100 text-yellow-700' : 
                            'bg-green-100 text-green-700'
                        }`}>
                          {r.status === 'new' ? 'Ny' : r.status === 'investigating' ? 'Pågår' : 'Løst'}
                        </span>
                      </div>
                    ))}
                    {reports.length === 0 && <p className="text-gray-400 text-sm italic">Ingen varsler registrert.</p>}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* UTREDNINGER */}
          {activeTab === 'utredninger' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
              <div className="bg-white p-8 rounded-[32px] border border-blue-50 shadow-sm">
                <h3 className="font-black uppercase tracking-tight mb-6">Utredningsoversikt</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  <div className="p-6 bg-blue-50 rounded-2xl">
                    <p className="text-xs font-bold text-blue-400 uppercase tracking-widest">Totalt antall</p>
                    <p className="text-4xl font-black text-blue-900 mt-2">{investigations.length}</p>
                  </div>
                  <div className="p-6 bg-orange-50 rounded-2xl">
                    <p className="text-xs font-bold text-orange-400 uppercase tracking-widest">Pågående</p>
                    <p className="text-4xl font-black text-orange-700 mt-2">{investigations.filter(i => i.status === 'in_progress').length}</p>
                  </div>
                  <div className="p-6 bg-green-50 rounded-2xl">
                    <p className="text-xs font-bold text-green-500 uppercase tracking-widest">Fullførte</p>
                    <p className="text-4xl font-black text-green-700 mt-2">{investigations.filter(i => i.status === 'completed').length}</p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* FINANS */}
          {activeTab === 'finans' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
              <div className="bg-white rounded-[32px] border border-blue-50 shadow-sm overflow-hidden">
                <div className="p-8 border-b border-blue-50 flex justify-between items-center">
                  <h3 className="font-black uppercase tracking-tight">Inntekt per Abonnement</h3>
                  <SafeIcon icon={FiDollarSign} className="text-blue-600" />
                </div>
                <div className="p-8 space-y-6">
                  {['Standard', 'Konsern', 'Grunnpakke'].map(plan => {
                    const planComps = companies.filter(c => c.subscription === plan.toLowerCase());
                    const income = planComps.reduce((sum, c) => sum + (c.monthly_fee || c.monthlyFee || 0), 0);
                    const percentage = stats.mrr > 0 ? (income / stats.mrr) * 100 : 0;
                    return (
                      <div key={plan} className="space-y-2">
                        <div className="flex justify-between text-sm font-bold">
                          <span>{plan}</span>
                          <span>{income.toLocaleString()} kr ({percentage.toFixed(0)}%)</span>
                        </div>
                        <div className="w-full bg-blue-50 h-3 rounded-full overflow-hidden">
                          <div className="bg-blue-600 h-full" style={{ width: `${percentage}%` }}></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          )}

          {/* BOOKING & DEMOER */}
          {activeTab === 'booking' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
              <div className="bg-white p-8 rounded-[32px] border border-blue-50 shadow-sm">
                <h3 className="font-black uppercase tracking-tight mb-6 flex items-center gap-2">
                  <SafeIcon icon={FiCalendar} className="text-indigo-600" /> Kommende og gjennomførte bookinger
                </h3>
                <div className="space-y-4">
                  {bookings.length > 0 ? (
                    bookings.sort((a,b) => new Date(b.date) - new Date(a.date)).map(b => (
                      <div key={b.id} className="flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 bg-gray-50 rounded-2xl border border-gray-100 gap-4">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-xl flex items-center justify-center">
                            <SafeIcon icon={FiCalendar} />
                          </div>
                          <div>
                            <p className="font-bold text-gray-900">{b.customer_name || b.customerName}</p>
                            <p className="text-xs text-gray-500">{new Date(b.date).toLocaleDateString('no-NO')} - Kl. {b.time}</p>
                          </div>
                        </div>
                        <span className={`px-4 py-1.5 rounded-lg text-xs font-black uppercase tracking-widest ${b.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                          {b.status || 'Pending'}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-gray-400 italic">Ingen bookinger funnet i systemet.</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* PARTNERE & SELGERE */}
          {activeTab === 'salg' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white p-8 rounded-[32px] border border-blue-50 shadow-sm">
                  <h3 className="font-black uppercase tracking-tight mb-6 flex items-center gap-2">
                    <SafeIcon icon={FiBriefcase} className="text-blue-600" /> Partneroversikt
                  </h3>
                  <div className="space-y-3">
                    {partners.length > 0 ? partners.map(p => (
                      <div key={p.id} className="p-4 border border-gray-100 bg-gray-50 rounded-2xl flex justify-between items-center">
                        <div>
                          <p className="font-bold text-gray-900">{p.name}</p>
                          <p className="text-xs text-gray-500">{p.email}</p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase ${p.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {p.active ? 'Aktiv' : 'Inaktiv'}
                          </span>
                        </div>
                      </div>
                    )) : <p className="text-gray-400 italic text-center py-8">Ingen partnere funnet.</p>}
                  </div>
                </div>
                
                <div className="bg-white p-8 rounded-[32px] border border-blue-50 shadow-sm">
                  <h3 className="font-black uppercase tracking-tight mb-6 flex items-center gap-2">
                    <SafeIcon icon={FiUsers} className="text-blue-600" /> Selgeroversikt
                  </h3>
                  <div className="space-y-3">
                    {sellers.length > 0 ? sellers.map(s => (
                      <div key={s.id} className="p-4 border border-gray-100 bg-gray-50 rounded-2xl flex justify-between items-center">
                        <div>
                          <p className="font-bold text-gray-900">{s.name}</p>
                          <p className="text-xs text-gray-500">{s.email}</p>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase ${s.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {s.active ? 'Aktiv' : 'Inaktiv'}
                          </span>
                        </div>
                      </div>
                    )) : <p className="text-gray-400 italic text-center py-8">Ingen selgere funnet.</p>}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* EKSPORT */}
          {activeTab === 'eksport' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-3xl mx-auto p-12 text-center space-y-8 bg-white rounded-[48px] shadow-sm border border-blue-50">
              <div className="w-24 h-24 bg-blue-50 text-blue-600 rounded-[32px] flex items-center justify-center mx-auto shadow-inner">
                <SafeIcon icon={FiDownload} size={40} />
              </div>
              <div>
                <h2 className="text-2xl font-black uppercase tracking-tight">Generer dataeksport</h2>
                <p className="text-gray-500 text-sm mt-2">Velg format og periode for å laste ned rådata til regnskap eller analyse.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 h-40">
                <button onClick={() => handleExport('csv')} className="bg-blue-50 hover:bg-blue-100 rounded-3xl p-6 flex flex-col items-center justify-center gap-3 transition-all border border-blue-100 group">
                  <SafeIcon icon={FiFileText} size={32} className="text-blue-600 group-hover:scale-110 transition-transform" />
                  <span className="text-xs font-black uppercase text-blue-900 tracking-widest">CSV-Fil</span>
                </button>
                <button onClick={() => handleExport('excel')} className="bg-green-50 hover:bg-green-100 rounded-3xl p-6 flex flex-col items-center justify-center gap-3 transition-all border border-green-100 group">
                   <SafeIcon icon={FiLayout} size={32} className="text-green-600 group-hover:scale-110 transition-transform" />
                  <span className="text-xs font-black uppercase text-green-900 tracking-widest">Excel (XLSX)</span>
                </button>
                <button onClick={() => handleExport('pdf')} className="bg-red-50 hover:bg-red-100 rounded-3xl p-6 flex flex-col items-center justify-center gap-3 transition-all border border-red-100 group">
                  <SafeIcon icon={FiFileText} size={32} className="text-red-600 group-hover:scale-110 transition-transform" />
                  <span className="text-xs font-black uppercase text-red-900 tracking-widest">PDF Rapport</span>
                </button>
              </div>
            </motion.div>
          )}

        </div>
      </main>
    </div>
  );
};

export default ReportingModule;